import React from 'react';
import { Box, Typography, Paper, Grid, Alert, Divider, List, ListItem, ListItemIcon, ListItemText, Chip, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import {
  CheckCircle as CheckIcon,
  AccountBalanceWallet as AccountingIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as EarningsIcon,
  TrendingDown as DebtIcon,
  Receipt as LedgerIcon,
  Paid as PaidIcon,
  Balance as BalanceIcon,
  Groups as PlayersIcon,
} from '@mui/icons-material';
import { SupportLayout, InfoCard, VisualExample, Formula, MoneyDisplay } from '../../components/support';
import { useDocumentTitle } from '../../hooks/core';

export const AccountingSupportPage: React.FC = () => {
  useDocumentTitle('Accounting & Ledger - Support');

  return (
    <SupportLayout
      title="Accounting & Ledger"
      description="Track payments, winnings, and balances for all players across your boards. Learn how SquareUp's accounting system works."
      breadcrumbs={[{ label: 'Accounting & Ledger' }]}
    >
      {/* Overview */}
      <Box sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Understanding the Ledger
        </Typography>
        <Typography variant="body1" paragraph>
          SquareUp's accounting system tracks every financial transaction in your pool. It automatically calculates who owes what, who has won, and what the current balance is for each player.
        </Typography>

        <Paper elevation={0} sx={{ p: 3, bgcolor: 'primary.50', border: 1, borderColor: 'primary.200' }}>
          <Typography variant="h6" gutterBottom>Key Concepts</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <DebtIcon sx={{ fontSize: 40, color: 'error.main', mb: 1 }} />
                <Typography variant="subtitle2" fontWeight="bold">Owed</Typography>
                <Typography variant="body2" color="text.secondary">Money player owes for squares</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <PaidIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                <Typography variant="subtitle2" fontWeight="bold">Paid</Typography>
                <Typography variant="body2" color="text.secondary">Money received from player</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <EarningsIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                <Typography variant="subtitle2" fontWeight="bold">Winnings</Typography>
                <Typography variant="body2" color="text.secondary">Money player has won</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <BalanceIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                <Typography variant="subtitle2" fontWeight="bold">Balance</Typography>
                <Typography variant="body2" color="text.secondary">Net amount (+ or -)</Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Ledger Entries */}
      <Box id="ledger-entries" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <LedgerIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Ledger Entry Types
        </Typography>

        <Typography variant="body1" paragraph>
          The ledger tracks three types of transactions:
        </Typography>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper elevation={0} sx={{ p: 3, border: 2, borderColor: 'error.main', height: '100%' }}>
              <Chip label="Square Purchase" color="error" sx={{ mb: 2 }} />
              <Typography variant="body2" paragraph>
                Automatically created when a player claims a square. This is a <strong>negative</strong> entry (money owed).
              </Typography>
              <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">Example:</Typography>
                <Typography variant="body1" fontWeight="bold" color="error.main">
                  -$10.00
                </Typography>
                <Typography variant="caption">Claimed square (3, 7)</Typography>
              </Box>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper elevation={0} sx={{ p: 3, border: 2, borderColor: 'success.main', height: '100%' }}>
              <Chip label="Payout" color="success" sx={{ mb: 2 }} />
              <Typography variant="body2" paragraph>
                Automatically created when a player wins. This is a <strong>positive</strong> entry (money owed to them).
              </Typography>
              <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">Example:</Typography>
                <Typography variant="body1" fontWeight="bold" color="success.main">
                  +$100.00
                </Typography>
                <Typography variant="caption">Q1 Winner</Typography>
              </Box>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper elevation={0} sx={{ p: 3, border: 2, borderColor: 'info.main', height: '100%' }}>
              <Chip label="Manual Payment" color="info" sx={{ mb: 2 }} />
              <Typography variant="body2" paragraph>
                Created by you when a player pays (or you pay them). Can be positive or negative.
              </Typography>
              <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">Example:</Typography>
                <Typography variant="body1" fontWeight="bold" color="info.main">
                  +$50.00
                </Typography>
                <Typography variant="caption">Received via Venmo</Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Balance Calculation */}
      <Box id="balance-calculation" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <BalanceIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          How Balance is Calculated
        </Typography>

        <Typography variant="body1" paragraph>
          A player's <strong>net balance</strong> tells you whether they owe money or are owed money:
        </Typography>

        <Formula
          formula="Net Balance = Winnings - Owed + Paid"
          variables={[
            { name: 'Winnings', value: '+', description: 'Money won from payouts' },
            { name: 'Owed', value: '-', description: 'Cost of claimed squares' },
            { name: 'Paid', value: '+', description: 'Payments received from player' },
          ]}
        />

        <VisualExample title="Balance Calculation Example" caption="Player with 5 squares at $10 each, won Q2">
          <Grid container spacing={3} alignItems="center">
            <Grid size={{ xs: 12, md: 8 }}>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell>Squares claimed (5 × $10)</TableCell>
                    <TableCell align="right" sx={{ color: 'error.main', fontWeight: 'bold' }}>-$50.00</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Q2 Winner payout</TableCell>
                    <TableCell align="right" sx={{ color: 'success.main', fontWeight: 'bold' }}>+$200.00</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Payment received (Venmo)</TableCell>
                    <TableCell align="right" sx={{ color: 'info.main', fontWeight: 'bold' }}>+$50.00</TableCell>
                  </TableRow>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Net Balance</TableCell>
                    <TableCell align="right" sx={{ color: 'success.main', fontWeight: 'bold', fontSize: '1.1em' }}>+$200.00</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper sx={{ p: 2, bgcolor: 'success.50', textAlign: 'center' }}>
                <Typography variant="body2" color="success.main" fontWeight="bold">
                  You Owe This Player
                </Typography>
                <Typography variant="h4" color="success.main" fontWeight="bold">
                  $200
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </VisualExample>

        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'error.main', bgcolor: 'error.50' }}>
              <Typography variant="subtitle2" color="error.main" fontWeight="bold" gutterBottom>
                Negative Balance = Player Owes You
              </Typography>
              <Typography variant="body2">
                The player hasn't paid enough for their squares, or their winnings don't cover what they owe.
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'success.main', bgcolor: 'success.50' }}>
              <Typography variant="subtitle2" color="success.main" fontWeight="bold" gutterBottom>
                Positive Balance = You Owe Player
              </Typography>
              <Typography variant="body2">
                The player has winnings or overpaid. You need to pay them the difference.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Recording Payments */}
      <Box id="recording-payments" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <MoneyIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Recording Payments
        </Typography>

        <Typography variant="body1" paragraph>
          When a player pays you (or you pay them), record it in the ledger to keep balances accurate.
        </Typography>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider' }}>
              <Typography variant="h6" gutterBottom>Recording Money Received</Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Go to board's Ledger tab" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Click on the player's name" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Click 'Record Payment'" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Enter amount as POSITIVE number" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Add note (e.g., 'Venmo 1/15')" />
                </ListItem>
              </List>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider' }}>
              <Typography variant="h6" gutterBottom>Recording Money Paid Out</Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Same process as above" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Enter amount as NEGATIVE number" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Or use 'Mark Winnings Paid' button" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Add note (e.g., 'Paid Q1 winnings')" />
                </ListItem>
              </List>
            </Paper>
          </Grid>
        </Grid>

        <InfoCard title="Quick Settlement" variant="tip" icon={<PaidIcon />}>
          <Typography variant="body2">
            For players who are owed winnings, you can use the <strong>"Mark Winnings Paid"</strong> button to quickly record that you've paid out their earnings. This automatically creates the right ledger entry.
          </Typography>
        </InfoCard>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Manager Dashboard */}
      <Box id="manager-view" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <PlayersIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Manager Accounting Dashboard
        </Typography>

        <Typography variant="body1" paragraph>
          The <strong>Accounting</strong> page (accessible from the main menu) shows aggregated data across <strong>all your completed boards</strong>:
        </Typography>

        <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', mb: 3 }}>
          <Typography variant="h6" gutterBottom>Dashboard Summary</Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 6, sm: 3 }}>
              <MoneyDisplay label="To Collect" amount={450} variant="negative" />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <MoneyDisplay label="To Pay Out" amount={1250} variant="positive" />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <MoneyDisplay label="Completed Boards" amount={3} variant="neutral" />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <MoneyDisplay label="Total Players" amount={45} variant="neutral" />
            </Grid>
          </Grid>
        </Paper>

        <Typography variant="body1" paragraph>
          Below the summary, you'll see a list of all players with their aggregated balances. Click any player to see their detailed history across all your boards.
        </Typography>

        <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Player</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Boards</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Owed</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Paid</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Winnings</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Net Balance</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>John D.</TableCell>
                <TableCell align="right">3</TableCell>
                <TableCell align="right">$150</TableCell>
                <TableCell align="right" sx={{ color: 'success.main' }}>$150</TableCell>
                <TableCell align="right" sx={{ color: 'info.main' }}>$200</TableCell>
                <TableCell align="right" sx={{ color: 'success.main', fontWeight: 'bold' }}>+$200</TableCell>
                <TableCell align="center"><Chip label="Pending" size="small" color="warning" /></TableCell>
              </TableRow>
              <TableRow sx={{ bgcolor: 'success.50' }}>
                <TableCell>Sarah M.</TableCell>
                <TableCell align="right">2</TableCell>
                <TableCell align="right">$100</TableCell>
                <TableCell align="right" sx={{ color: 'success.main' }}>$100</TableCell>
                <TableCell align="right" sx={{ color: 'info.main' }}>$0</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>$0</TableCell>
                <TableCell align="center"><Chip label="Settled" size="small" color="success" /></TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Mike R.</TableCell>
                <TableCell align="right">1</TableCell>
                <TableCell align="right">$50</TableCell>
                <TableCell align="right" sx={{ color: 'success.main' }}>$0</TableCell>
                <TableCell align="right" sx={{ color: 'info.main' }}>$0</TableCell>
                <TableCell align="right" sx={{ color: 'error.main', fontWeight: 'bold' }}>-$50</TableCell>
                <TableCell align="center"><Chip label="Owes Money" size="small" color="error" /></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Player View */}
      <Box id="player-view" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <AccountingIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          My Balances (Player View)
        </Typography>

        <Typography variant="body1" paragraph>
          Players can see their own balances through the <strong>My Balances</strong> page. This shows what they owe or are owed across all boards they participate in.
        </Typography>

        <InfoCard title="What Players See" variant="info">
          <List dense disablePadding>
            <ListItem disablePadding sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
              <ListItemText primary="Balances grouped by board manager" />
            </ListItem>
            <ListItem disablePadding sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
              <ListItemText primary="Details per board (squares, winnings, payments)" />
            </ListItem>
            <ListItem disablePadding sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
              <ListItemText primary="Complete transaction history" />
            </ListItem>
            <ListItem disablePadding sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
              <ListItemText primary="Settlement status with each manager" />
            </ListItem>
          </List>
        </InfoCard>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Cash Flow */}
      <Box id="cash-flow" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <EarningsIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Cash Flow Tracking
        </Typography>

        <Typography variant="body1" paragraph>
          The accounting dashboard includes cash flow statistics to help you understand money movement over time:
        </Typography>

        <Grid container spacing={2}>
          {[
            { period: 'Last 7 Days', received: 250, paidOut: 100, net: 150 },
            { period: 'Last 30 Days', received: 800, paidOut: 450, net: 350 },
            { period: 'This Year', received: 2500, paidOut: 1800, net: 700 },
            { period: 'All Time', received: 5000, paidOut: 4200, net: 800 },
          ].map((item) => (
            <Grid size={{ xs: 6, sm: 3 }} key={item.period}>
              <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider', textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">{item.period}</Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2" color="success.main">+${item.received}</Typography>
                <Typography variant="body2" color="error.main">-${item.paidOut}</Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" fontWeight="bold" color={item.net >= 0 ? 'success.main' : 'error.main'}>
                  Net: ${item.net}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Alert severity="info" sx={{ mt: 3 }}>
          Cash flow shows actual money movement (payments received and paid out), not theoretical balances. Use this to track your real-world cash position.
        </Alert>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Offline Players */}
      <Box id="offline-accounting" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Offline Player Accounting
        </Typography>

        <Typography variant="body1" paragraph>
          Offline players appear in the same accounting views as registered users. You can record payments and track their balances just like any other player.
        </Typography>

        <Paper elevation={0} sx={{ p: 3, bgcolor: 'grey.50', border: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Offline players are marked with "(offline)" in the accounting views:
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
            <Chip label="Uncle Bob (offline)" variant="outlined" />
            <Chip label="Grandma Jean (offline)" variant="outlined" />
            <Chip label="Neighbor Mike (offline)" variant="outlined" />
          </Box>
        </Paper>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Board Players Tab */}
      <Box id="players-tab" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <BalanceIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Board Players Tab
        </Typography>

        <Typography variant="body1" paragraph>
          The <strong>Players</strong> tab on each board's detail page shows real-time financial status for all players in that specific board. It combines player management with payment tracking.
        </Typography>

        <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', mb: 3 }}>
          <Typography variant="h6" gutterBottom>What You'll See</Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Player List</Typography>
              <List dense disablePadding>
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary="All board participants (online and offline)" />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary="Search and filter by balance status" />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary="Color-coded rows (green = paid/settled)" />
                </ListItem>
              </List>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Per-Player Details</Typography>
              <List dense disablePadding>
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary="Number of squares claimed" />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary="Amount owed, paid, won, and net balance" />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary="Expandable row to see winning details" />
                </ListItem>
              </List>
            </Grid>
          </Grid>
        </Paper>

        <InfoCard title="Manager Actions in Players Tab" variant="info" icon={<MoneyIcon />}>
          <Typography variant="body2" paragraph>
            Board managers have additional capabilities:
          </Typography>
          <List dense disablePadding>
            <ListItem disablePadding sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
              <ListItemText primary="Record payments when players pay you for their squares" />
            </ListItem>
            <ListItem disablePadding sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
              <ListItemText primary="Mark winnings as paid out to winners" />
            </ListItem>
            <ListItem disablePadding sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
              <ListItemText primary="Add and manage offline players" />
            </ListItem>
            <ListItem disablePadding sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
              <ListItemText primary="Promote players to co-managers" />
            </ListItem>
          </List>
        </InfoCard>

        <Alert severity="info" sx={{ mt: 3 }}>
          <strong>Tip:</strong> Players can see their own balance and transaction history for the board, but they can't see other players' payment details—only you (the manager) can see the full picture.
        </Alert>
      </Box>
    </SupportLayout>
  );
};

export default AccountingSupportPage;
